import React from 'react';
import PropTypes from 'prop-types';
import glamorous from 'glamorous';
import { Portal } from 'react-portal';

import { media, mixins } from '../../../styles';

import Button from '../Button';

const NAV_HEIGHT = 56;

const Backdrop = glamorous.div(props => ({
  ...mixins.fillToParent,
  ...mixins.scroll,

  zIndex: 10000,
  position: 'fixed',
  backgroundColor: 'rgba(0,0,0,0.87)',

  [media('sm', { isMax: true })]: {
    zIndex: 100,
    top: NAV_HEIGHT,
    height: `calc(100% - ${NAV_HEIGHT}px)`,
  },
}));

const POPOVER_WIDTH = 320;

const Container = glamorous.div(props => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'absolute',
  top: 0,
  maxWidth: props.popoverWidth,
  width: '100%',
  left: '50%',
  marginLeft: -(props.popoverWidth / 2),
  backgroundColor: '#fff',
  borderBottomLeftRadius: 3,
  borderBottomRightRadius: 3,
  padding: 16,

  marginBottom: 16,

  [media('sm', { isMax: true })]: {
    position: 'static',
    left: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: 0,
    borderRadius: 0,
    maxWidth: '100%',
    minHeight: '100%',
  },
}));

const Content = glamorous.div(props => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: props.hasOk ? 16 : undefined,
}));

const Actions = glamorous.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
});

const noop = () => {};
const stopPropagation = e => e.stopPropagation();

class Popover extends React.PureComponent {
  static propTypes = {
    onCancel: PropTypes.func,
    onOk: PropTypes.func,
    renderOpen: PropTypes.func,

    closeOnClickBackdrop: PropTypes.bool,
    hasClose: PropTypes.bool,
    hasOk: PropTypes.bool,

    popoverWidth: PropTypes.number,

    labelClose: PropTypes.string,
    labelOk: PropTypes.string,
  };

  static defaultProps = {
    onCancel: noop,
    onOk: noop,
    renderOpen: noop,

    closeOnClickBackdrop: false,
    hasClose: false,
    hasOk: true,

    popoverWidth: POPOVER_WIDTH,

    labelClose: 'Close',
    labelOk: 'Ok',
  };

  state = {
    isOpen: this.props.isOpen,
    isTransitioning: false,
  };

  componentDidMount() {
    if (this.props.isOpen) {
      this.setup();
    }
  }

  componentWillReceiveProps(nextProps) {
    const {
      isOpen,
    } = nextProps;

    if (this.isControlled && isOpen !== this.props.isOpen) {
      this.setIsOpen(isOpen);
    }

    document.body.classList.toggle('is-viewing-popover', isOpen);
  }

  componentWillUnmount() {
    this.destroy();
  }

  get isControlled() {
    return typeof this.props.isOpen !== 'undefined';
  }

  setup() {
    this.hasSetup = true;


    setTimeout(() => {
      document.addEventListener('keyup', this.handleKeyup);
    }, 600);
  }

  destroy() {
    this.hasSetup = false;
    document.body.classList.remove('is-viewing-popover');
    document.removeEventListener('keyup', this.handleKeyup);
  }

  setIsOpen = (isOpen) => {
    if (isOpen) {
      !this.hasSetup && this.setup();

      document.body.classList.add('is-viewing-popover');

      this.setState({
        isOpen: true,
        isTransitioning: true,
      });

      setTimeout(() => this.setState({ isTransitioning: false }), 250);
    } else {
      this.destroy();

      document.body.classList.remove('is-viewing-popover');

      this.setState({
        isOpen: false,
        isTransitioning: true,
      });

      setTimeout(() => this.setState({ isTransitioning: false }), 250);
    }
  };

  handleOk = (e) => {
    e.stopPropagation();

    if (!this.isControlled) {
      this.setIsOpen(false);
    }

    this.props.onOk(e);
  };

  handleClose = (e) => {
    e.stopPropagation();

    if (!this.isControlled) {
      this.setIsOpen(false);
    }

    this.props.onCancel(e);
  };

  handleKeyup = (e) => {
    if (!this.state.isOpen) {
      return this.destroy();
    }

    switch (e.key) {
      case 'Escape':
        return this.handleClose(e);
      case 'Enter':
        return this.handleOk(e);
      default:
        return null;
    }
  };

  handleOpen = () => {
    this.setIsOpen(true);
  };

  renderContent = () => {
    const {
      isOpen,
    } = this.state;

    const {
      children,
      closeOnClickBackdrop,
      hasClose,
      hasOk,
      labelClose,
      labelOk,
      popoverWidth,
    } = this.props;

    return (
      <Backdrop
        className={isOpen ? 'animated fadeIn' : 'animated fadeOut'}
        onClick={closeOnClickBackdrop ? this.handleClose : undefined}
        innerRef={this.props.popoverScrollRef}
      >
        <Container
          onClick={stopPropagation}
          className={isOpen ? 'animated slideInDown' : 'animated slideOutUp'}
          popoverWidth={popoverWidth}
        >
          <Content hasOk={hasOk}>{children}</Content>

          <Actions>
            {hasClose && (
              <Button
                onClick={this.handleClose}
                label={labelClose}
                marginRight={16}
              />
            )}

            {hasOk && (
              <Button
                onClick={this.handleOk}
                label={labelOk}
                color="primary"
              />
            )}
          </Actions>
        </Container>
      </Backdrop>
    );
  };

  render() {
    const {
      isOpen,
      isTransitioning,
    } = this.state;

    const {
      renderOpen
    } = this.props;

    return (
      <React.Fragment>
        {renderOpen && renderOpen(this.handleOpen, isOpen)}
        {(isOpen || isTransitioning) && (
          <Portal>
          {this.renderContent()}
          </Portal>
        )}
      </React.Fragment>
    );
  }
}

export default Popover;



// WEBPACK FOOTER //
// ./src/components/ui/Popover/Popover.js